/**
 * Breakeven teaching pairs — level-keyed config over authored gate data.
 *
 * Sim owns the math (`applyGateOp` / `breakevenAddVsMul`); this module only
 * points at level-data gate ids so presentation / tooling can highlight
 * teaching moments. Not evaluated per frame — loaded once per level id.
 *
 * Classic trap: +A vs ×M. Prefer + when N < A/(M-1); prefer × above.
 * Naive "always multiply" fails at low N; naive "always big +" fails high.
 */

export interface BreakevenTeachingPair {
  /** Level document id (`level-01` … `level-05`). */
  readonly levelId: string;
  /** Left / right gate ids from level JSON (same z, opposite lanes). */
  readonly leftGateId: string;
  readonly rightGateId: string;
  /**
   * Human-readable teaching note (not shown in-game by default).
   * Documents the flip: low N → +, high N → ×.
   */
  readonly note: string;
}

/**
 * Teaching pairs sourced from content-schema level gates.
 * Keys are level ids; values list pairs where the naive pick can be wrong.
 */
export const BREAKEVEN_TEACHING_BY_LEVEL: Readonly<
  Record<string, readonly BreakevenTeachingPair[]>
> = {
  // L2: do-the-math intro — +20 vs ×2, breakeven at N=20
  "level-02": [
    {
      levelId: "level-02",
      leftGateId: "l2-pair-add",
      rightGateId: "l2-pair-mul",
      note: "+20 vs ×2; breakeven N=20. At low N after start, + wins (naive × is wrong).",
    },
  ],
  // L4: early +20 vs ×2, then later +12 vs ×2 after growth (× often better)
  "level-04": [
    {
      levelId: "level-04",
      leftGateId: "l4-pair-add",
      rightGateId: "l4-pair-mul",
      note: "+20 vs ×2 early; low N prefers +.",
    },
    {
      levelId: "level-04",
      leftGateId: "l4-pair3-add",
      rightGateId: "l4-pair3-mul",
      note: "+12 vs ×2 after growth; high N flips to × (naive + is wrong).",
    },
  ],
  // L5 ad showcase: same early/late flip structure
  "level-05": [
    {
      levelId: "level-05",
      leftGateId: "l5-pair-add",
      rightGateId: "l5-pair-mul",
      note: "+20 vs ×2; teach low-N prefer +.",
    },
    {
      levelId: "level-05",
      leftGateId: "l5-pair3-add",
      rightGateId: "l5-pair3-mul",
      note: "+12 vs ×2 late; high N prefers ×.",
    },
  ],
};

/** Look up teaching pairs for a level id (empty if none). */
export function breakevenPairsForLevel(
  levelId: string,
): readonly BreakevenTeachingPair[] {
  return BREAKEVEN_TEACHING_BY_LEVEL[levelId] ?? [];
}
